import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faSave, faTimes, faEllipsisVertical } from "@fortawesome/free-solid-svg-icons"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
import useLocalStorageState from "use-local-storage-state"
import { useGetRequest, usePostRequest } from "../../../Utils/hooks/QueryHandler"
import { GetRequest, DeleteRequest, PutRequest } from "../../../Utils/hooks/ApiHandler"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
// import { CKEditor } from "@ckeditor/ckeditor5-react"
// import ClassicEditor from "@ckeditor/ckeditor5-build-classic"
import ReactQuill from "react-quill";
import DeleteModal from "../../DeleteModal"
import { Dropdown } from "react-bootstrap"
import ExpandableNote from "../Contacts/ContactDetails"
import { FaStickyNote } from "react-icons/fa"
import nodata from "../../../Utils/assets/img/leads_no_data_found.png"


// Zod schema for note validation
const noteSchema = z.object({
  notes: z.string().min(1, "Note content is required").max(5000, "Note content is too long"),
})

export const Notes = ({ leadId, isActive = false, module_name, noteGrant }) => {
  const [notes, setNotes] = useState([])
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingNoteId, setEditingNoteId] = useState(null)
  const [showNoteActions, setShowNoteActions] = useState({})
  const [loading, setLoading] = useState({
    submit: false, // For create/update operations
    edit: false,   // For loading note data for editing
    delete: false, // For delete operations
    general: false // For general operations
  })
  const [notesContent, setNotesContent] = useState("")
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [noteToDelete, setNoteToDelete] = useState(null)
  const [closing, setClosing] = useState(false)
  const navigate = useNavigate()
  const dropdownRefs = useRef({})

  // React Hook Form setup with Zod validation
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(noteSchema),
    defaultValues: {
      notes: "",
    },
  })

  // Get customer data from session storage for authentication
  const [customer, setCustomer] = useLocalStorageState("customer", {
    token: {},
  })

  // Memoize customer data to prevent unnecessary re-renders
  const customerData = useMemo(() => ({
    customerId: customer?.customer_details?.customer_id,
    userLoginId: customer?.customer_details?.id,
    token: customer?.token?.authorization,
    rf: customer?.token?.authentication,
  }), [customer])

  // Setup headers for API requests
  const headers = useMemo(() => ({
    authentication: customerData.rf,
    Authorization: customerData.token,
  }), [customerData.rf, customerData.token])

  const extras = useMemo(() => ({
    customer,
    setCustomer,
    rf: customerData.rf,
    navigate
  }), [customer, setCustomer, customerData.rf, navigate])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(dropdownRefs.current).forEach(key => {
        if (dropdownRefs.current[key] && !dropdownRefs.current[key].contains(event.target)) {
          setShowNoteActions(prev => ({ ...prev, [key]: false }))
        }
      })
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // API hook for fetching notes from server - only enabled when tab is active
  const {
    data: notesData,
    error,
    refetch: fetchNotes,
    isError,
    isLoading: isLoadingNotes
  } = useGetRequest(
    `lead/fetchNotes/${leadId}/${customerData.customerId}/${module_name}`,
    "getNotes",
    headers,
    extras,
    { enabled: isActive && !!leadId && !!customerData.customerId }
  )

  // API hook for adding new notes
  const { mutate: addNote } = usePostRequest(`lead/createNote`, false, { headers }, extras)

  // Effect hook to update notes state when API data changes
  useEffect(() => {
    if (notesData?.status === "success") {
      setNotes(notesData?.data || [])
    } else if (isError) {
      // Handle error fetching notes
    }
  }, [notesData, isError, error])

  // Reset form and state
  const resetForm = useCallback(() => {
    setNotesContent("")
    setEditingNoteId(null)
    setShowAddForm(false)
    reset()
  }, [reset])

  // Handle CKEditor content change
  // const handleEditorChange = useCallback((event, editor) => {
  //   setNotesContent(data)
  //   setValue("notes", data, { shouldValidate: true })
  // }, [setValue])

  // Submit handler for adding/editing notes
  const onSubmit = useCallback(async (formData) => {
    if (!notesContent.trim()) {
      toast.error("Please enter some notes before submitting")
      return
    }

    const payload = {
      leads_id: leadId,
      notes: notesContent,
      user_login_id: customerData.userLoginId,
      module_name: module_name
    }

    try {
      setLoading(true)

      if (editingNoteId) {
        // Update existing note using PutRequest
        const response = await PutRequest(
          "lead/updateNote",
          { ...payload, note_id: editingNoteId },
          { headers },
          extras,
        )

        if (response.status === "success") {
          toast.success("Note updated successfully")
          fetchNotes()
          resetForm()
        } else {
          toast.error(response.message || "Failed to update note")
        }
      } else {
        // Create new note
        addNote(payload, {
          onSuccess: (res) => {
            if (res.status === "success") {
              toast.success("Note created successfully")
              fetchNotes()
              resetForm()
            } else {
              toast.error(res.message || "Failed to create note")
            }
          },
          onError: (err) => {
            toast.error(err?.message || "Failed to create note")
          },
        })
      }
    } catch (err) {
      toast.error("An unexpected error occurred")
    } finally {
      setLoading(false)
    }
  }, [notesContent, editingNoteId, leadId, customerData.userLoginId, headers, extras, addNote, fetchNotes, resetForm])

  // Toggle note actions dropdown
  const toggleNoteActions = useCallback((noteId) => {
    setShowNoteActions(prev => ({
      ...Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
      [noteId]: !prev[noteId]
    }))
  }, [])

  // Handle edit note - populate form with existing note data
  const handleEditNote = useCallback(async (note) => {
    try {
      setLoading(true)
      const response = await GetRequest(`lead/getNotesById/${note}/${leadId}/${module_name}`, { headers })
      const data = response.data;
      if (response.status == "success") {
        setNotesContent(data.notes)
        setValue("notes", data.notes)
        setEditingNoteId(data.id)
        setShowAddForm(true)
      } else {
        toast.error("Failed to load note for editing")
      }
    } catch (error) {
      toast.error("Error loading note")
    } finally {
      setLoading(false)
    }
    setShowNoteActions({})
  }, [leadId, headers, setValue])

  // Handle delete note confirmation
  const handleDeleteNote = useCallback((noteId) => {
    setNoteToDelete(noteId)
    setShowDeleteModal(true)
    setShowNoteActions({})
  }, [])

  // Confirm delete note
  const confirmDeleteNote = useCallback(async () => {
    if (!noteToDelete) return
    setLoading(true)
    try {
      setLoading(prev => ({ ...prev, delete: true }))
      const response = await DeleteRequest(`lead/deleteNotes/${leadId}/${noteToDelete.id}/${customerData.userLoginId}/${module_name}`, { headers }, extras)

      if (response.status === "success") {
        toast.success("Note deleted successfully")
        setLoading(false)
        fetchNotes()
      } else {
        setLoading(false)
        toast.error(response.message || "Failed to delete note")
      }
    } catch (error) {
      setLoading(false)
      toast.error("Error deleting note")
    } finally {
      setLoading(false)
      setLoading(prev => ({ ...prev, delete: false }))
    }

    setShowDeleteModal(false)
    setNoteToDelete(null)
  }, [noteToDelete, leadId, customerData.userLoginId, headers, extras, fetchNotes])

  // Close delete modal
  const handleCloseDeleteModal = useCallback(() => {
    setClosing(true)
    setTimeout(() => {
      setShowDeleteModal(false)
      setNoteToDelete(null)
      setClosing(false)
    }, 250)
  }, [])

  // Format date for display
  const formatDate = useCallback((dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
  }, [])

  // Toggle add form
  const toggleAddForm = useCallback(() => {
    setShowAddForm(!showAddForm)
    if (showAddForm) resetForm()
  }, [showAddForm, resetForm])

  return (
    <div style={{ minHeight: "calc(100vh - 10.5rem)", maxHeight: "calc(100vh - 10.5rem)", overflowY: "auto", overflowX: "hidden", position: 'relative' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="mb-0" style={{ fontSize: "19px", fontWeight: 300 }}>
          Notes
        </div>
        <div className="d-flex align-items-center gap-1">
          <button
            className="btn btn-default-outline"
            style={styles.buttons}
            onClick={() => {
              if (!noteGrant.isCreate) {
                toast.warning("You are not authorized for this action!")
                return
              }
              toggleAddForm()
            }}
          >
            <FontAwesomeIcon icon={faPlus} /> Add
          </button>
        </div>
      </div>

      <hr />
      {(loading.submit || loading.edit) && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            borderRadius: "0.375rem",
          }}
        >
          <div className="d-flex flex-column align-items-center">
            <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-2 text-primary fw-semibold">
              {loading.edit ? "Loading note..." : (editingNoteId ? "Updating note..." : "Creating note...")}
            </div>
          </div>
        </div>
      )}
      {showAddForm && (
        <div className="card mb-4" style={{ border: "1px solid #e0e4f8" }}>
          {/* Form-specific loading state */}
          {(loading.submit || loading.edit) && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 100,
                borderRadius: "0.375rem",
              }}
            >
              <div className="d-flex flex-column align-items-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          )}

          <div className="card-body p-3">
            <h6 className="card-title mb-3">{editingNoteId ? "Edit Note" : "Add New Note"}</h6>
            <form
              onSubmit={handleSubmit(onSubmit)}
              onKeyDown={(e) => {
                const isQuill = e.target.closest('.ql-editor')
                if (e.key === "Enter") {
                  e.preventDefault()
                  handleSubmit(onSubmit)()
                }
              }}
            >
              <div className="mb-3">
                <div className="ckeditor-container">
                  <ReactQuill
                    theme="snow"
                    value={notesContent}
                    onChange={(value) => {
                      setNotesContent(value)
                      setValue("notes", value)
                    }}
                    placeholder="Write your message..."
                    style={{ minHeight: "160px" }}
                    modules={{
                      toolbar: [
                        [{ header: [1, 2, false] }],
                        ["bold", "italic", "underline", "link"],
                        [{ list: "ordered" }, { list: "bullet" }],
                        ["blockquote", "code-block"],
                        ["clean"],
                      ],
                    }}
                  />
                </div>
                {errors.notes && (
                  <p className="text-danger mt-1" style={{ fontSize: "12px" }}>
                    {errors.notes.message}
                  </p>
                )}
              </div>

              <div className="d-flex gap-2 justify-content-end">
                <button type="button" className="btn btn-default-outline" onClick={resetForm}>
                  <FontAwesomeIcon icon={faTimes} className="me-1" />
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-default"
                  disabled={loading.submit || loading.edit}
                >
                  {loading.submit ? (
                    <>
                      <div className="spinner-border spinner-border-sm me-1" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      {editingNoteId ? "Updating..." : "Saving..."}
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faSave} className="me-1" />
                      {editingNoteId ? "Update Note" : "Save Note"}
                    </>
                  )}
                </button>
              </div>
            </form>


          </div>
        </div>
      )}

      <div className="listStyleForContact" style={{ maxHeight: "calc(100vh - 12rem)", minHeight: "calc(100vh - 12rem)", overflowX: "auto", scrollBehavior: "smooth", scrollbarWidth: "thin" }}
        onMouseEnter={(e) => {
          e.currentTarget.style.scrollbarColor = "#e0e0e0 white"
        }}
      >
        {notes.length > 0 ? (
          notes.map((note) => (
            <div
              key={note.id}
              className="p-3 border-bottom"
            >
              <div className="d-flex justify-content-between align-items-start">
                <div
                  style={{
                    fontSize: "14px",
                    color: "black",
                  }}
                  className="d-flex"
                >
                  <FaStickyNote
                    size={18}
                    style={{
                      minWidth: 18,
                      marginRight: 10,
                      marginTop: 4,
                    }}
                  />
                  <div
                    style={{
                      fontSize: "14px",
                      color: "black",
                    }}
                  >
                    <ExpandableNote
                      key={note.id}
                      note={note}
                    />
                  </div>
                </div>
                <Dropdown className="dropdropdownsWithoutToggle">
                  <Dropdown.Toggle
                    variant="none"
                    id={`dropdown-note-${note.id}`}
                    style={{
                      border: "none",
                      background: "none",
                      padding: 0,
                    }}
                    className="dropdownTogglesBtns ms-3"
                  >
                    <FontAwesomeIcon
                      icon={faEllipsisVertical}
                    />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item
                      onClick={() => {
                        if (!noteGrant.isEdit) {
                          toast.warning("You are not authorized for this action!")
                          return
                        }
                        handleEditNote(note.id)
                      }
                      }
                    >
                      Edit
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => {
                        if (!noteGrant.isDelete) {
                          toast.warning("You are not authorized for this action!")
                          return
                        }
                        setNoteToDelete(note);
                        setShowDeleteModal(true);
                      }}
                    >
                      Delete
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              <p
                className="mb-0 mt-3 d-flex justify-content-between"
                style={{
                  fontWeight: 300,
                  fontSize: 11,
                  color: "#aeaeae",
                }}
              >
                {note.added_name || "-"}
                <span>
                  {new Date(
                    note.created_at
                  ).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}{" "}
                  | at{" "}
                  {new Date(
                    note.created_at
                  ).toLocaleString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true, // for AM/PM format
                  })}
                </span>
              </p>
            </div>
          ))
        ) : (
          <div
            class="d-flex justify-content-center align-items-center"
            style={{ position: "absolute", left: " 24%" }}
          >
            <img
              src={nodata}
              alt=""
              style={{ width: "17rem", marginTop: "0" }}
            />
          </div>
        )}
      </div>

      <DeleteModal
        show={showDeleteModal}
        onClose={() => {
          handleCloseDeleteModal()
        }}
        onConfirm={confirmDeleteNote}
        isLoading={loading}
        message="Are you sure you want to delete this Note?"
      />
      <style jsx>

        {
          `
            .ql-toolbar.ql-snow + .ql-container.ql-snow{
              max-height : 10rem;
              overflow-x : scroll;
              scroll-behaviour : smooth;
            }
          `
        }
      </style>
    </div>
  )
}

const styles = {
  buttons: {
    color: "#fff",
    fontSize: "13px",
    height: "34px",
    borderRadius: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

}
